// Load matching tab (ST-00218 P2): the route drawer (SPEC §5 UI 3) — GET /routes/:id.
//   (a) Changed loads: every dispatch whose figure effect differs between the current and the
//       new matching, with the reason;
//   (b) Data quality: registration keyed incorrectly (probable typo / delivery no only /
//       unconfirmed tier 4), delivery no blank / not found / pointing elsewhere, missing loads
//       beside the receipts that could be them, loads that need review (engine 1.1.0: the truck
//       was dispatched again before a receipt — probable duplicate weighing → "exclude the
//       duplicate ticket"; dispatched again → "open both tickets"), receipts with no dispatch,
//       received off-route, late, re-weighs — each with "amend" links into the Amendments page;
//       ST-00218 P3: "Location probably wrong" (tickets keyed with the wrong dispatch / receipt
//       location, or a location to add to the route — LocationSuggestionsList);
//   (c) Transit time: histogram, the learned / frozen window and the override field (manage);
//   (d) Sign-off and matching: sign off (optional, also in a batch from the table — it records
//       the review and gates nothing, ST-00218 P3 / SPEC §10), set the route to Truck + time
//       or back to delivery-number matching (the same setting as Settings → Route Groupings →
//       Load matching), the history, and the signed document (CSV / PDF).
// load_matching.manage gates every action; .view users get the same drawer read-only.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { FaFileCsv, FaFilePdf, FaTimes } from 'react-icons/fa';
import { useAuth } from '../../../../context/AuthContext';
import { formatAppDateTime, formatMiningDate, formatMiningTime } from '../../../../utils/dateUtils';
import {
  downloadExport,
  downloadSignoffExport,
  getRoute,
  loadMatchingErrorCode,
  loadMatchingErrorText,
  revertRoute,
  switchRoute,
  type ExportFormat,
} from '../../../../services/loadMatching';
import type {
  ChangeReasonCode,
  ChangedTicket,
  DqImplausibleItem,
  DqMissingItem,
  DqNeedsReviewItem,
  DqPairItem,
  DqTicketItem,
  ListOf,
  LoadMatchingMode,
  RouteDrawerResponse,
  RouteWindow,
  SemanticChange,
  SignoffRecord,
  SwitchResponse,
  TicketFlag,
  TicketRef,
} from '../../../../types/loadMatching';
import { bridgeSentence } from './HeadlineStrip';
import LocationSuggestionsList from './LocationSuggestionsList';
import { RouteNotes } from './RouteTable';
import SignoffDialog from './SignoffDialog';
import { TransitHistogram, WindowPanel } from './TransitSection';
import {
  FLAG_LABELS,
  MATCH_MODE_LABELS,
  NEW_STATUS_LABELS,
  OLD_CLASS_LABELS,
  REASON_LABELS,
  TIER_LABELS,
  dqIssueCount,
  fmtN,
  fmtPct,
  fmtPeriod,
  fmtPts,
  fmtT,
  fmtTransit,
  isNa,
  regDiffLabel,
} from './format';
import { AmendLinkButton, DqBadge, HintText, SignoffBadge, TierLegend, TierMixBar } from './parts';

export interface RouteDrawerProps {
  groupingId: number;
  period: { startDate: string; endDate: string };
  mode: LoadMatchingMode;
  canManage: boolean;
  semanticChanges: SemanticChange[];
  onClose: () => void;
  /** Sign-off, switch, revert or a window change happened: the caller refreshes the table. */
  onChanged: () => void;
}

type DrawerTab = 'changes' | 'quality' | 'transit' | 'signoff';

const ticketTime = (iso: string): string => `${formatMiningDate(iso)} ${formatMiningTime(iso)}`;

const capitalise = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

function TicketLine({ t, label }: { t: TicketRef; label?: string }) {
  return (
    <span className="text-xs text-slate-700">
      {label && <span className="text-slate-500">{label} </span>}
      <span className="font-mono font-medium text-slate-900">{t.ticketNo || '(no ticket no)'}</span>
      <span className="text-slate-400"> · </span>
      {ticketTime(t.timestamp)}
      <span className="text-slate-400"> · </span>
      <span className="font-mono">{t.regNo || '(no reg)'}</span>
      <span className="text-slate-400"> · </span>
      {fmtT(t.nettWeight)}
      {t.ticketType === 'Receipt' && (
        <>
          <span className="text-slate-400"> · delivery no </span>
          <span className="font-mono">{t.deliveryNo || 'blank'}</span>
        </>
      )}
    </span>
  );
}

function Flags({ flags }: { flags: TicketFlag[] }) {
  if (!flags.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {flags.map((f) => (
        <span
          key={f}
          className="px-1.5 py-px rounded bg-slate-100 text-slate-600 text-[10px] whitespace-nowrap"
        >
          {FLAG_LABELS[f] ?? f}
        </span>
      ))}
    </span>
  );
}

function Truncated({ list }: { list: ListOf<unknown> }) {
  if (!list.truncated) return null;
  return (
    <p className="text-[11px] text-slate-500 mt-1">
      Showing {fmtN(list.items.length)} of {fmtN(list.total)}. The export has the full list.
    </p>
  );
}

// ─────────────────────────────────────────────────────── (a) changed loads ──

function ChangedLoads({ list }: { list: ListOf<ChangedTicket> }) {
  const counts = useMemo(() => {
    const m = new Map<ChangeReasonCode, number>();
    for (const c of list.items) m.set(c.reasonCode, (m.get(c.reasonCode) ?? 0) + 1);
    return m;
  }, [list.items]);
  const [filter, setFilter] = useState<ChangeReasonCode | 'all'>('all');
  const items = filter === 'all' ? list.items : list.items.filter((c) => c.reasonCode === filter);

  if (!list.total) {
    return (
      <p className="text-sm text-slate-600 py-6 text-center">
        No load changes: every dispatch counts the same way under both matchings.
      </p>
    );
  }
  return (
    <div>
      <p className="text-xs text-slate-600 mb-2">
        {fmtN(list.total)} dispatches count differently with the new matching.
      </p>
      {counts.size > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3" role="group" aria-label="Filter by reason">
          {(['all', ...counts.keys()] as const).map((code) => (
            <button
              key={code}
              type="button"
              aria-pressed={filter === code}
              onClick={() => setFilter(code)}
              className={`h-7 px-2.5 rounded-full border text-[11px] font-medium ${
                filter === code
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'bg-surface border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {code === 'all'
                ? `All ${fmtN(list.items.length)}`
                : `${REASON_LABELS[code]} ${fmtN(counts.get(code) ?? 0)}`}
            </button>
          ))}
        </div>
      )}
      <ul
        className="divide-y divide-slate-100 border-y border-slate-100"
        aria-label="Changed loads"
      >
        {items.map((c) => (
          <li key={c.dispatch.ticketUid} className="py-2.5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <TicketLine t={c.dispatch} label="Dispatch" />
              <span className="text-[11px] text-slate-600 whitespace-nowrap">
                {OLD_CLASS_LABELS[c.old]} <span aria-hidden="true">→</span>
                <span className="sr-only"> becomes </span>{' '}
                <span className="font-semibold text-slate-900">{NEW_STATUS_LABELS[c.new]}</span>
              </span>
            </div>
            <p className="text-xs text-slate-800 mt-0.5">
              <HintText text={capitalise(c.reason)} />
            </p>
            {c.partner && (
              <div className="mt-0.5">
                <TicketLine t={c.partner} label="Receipt" />
                {c.transitMinutes !== null && (
                  <span className="text-[11px] text-slate-500">
                    {' '}
                    · transit {fmtTransit(c.transitMinutes)}
                  </span>
                )}
              </div>
            )}
            {(c.tier !== null || c.flags.length > 0) && (
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-slate-500">
                {c.tier !== null && (
                  <span>
                    Tier {c.tier} · {TIER_LABELS[c.tier].short}
                  </span>
                )}
                <Flags flags={c.flags} />
              </div>
            )}
          </li>
        ))}
      </ul>
      <Truncated list={list} />
    </div>
  );
}

// ───────────────────────────────────────────────────────── (b) data quality ──

function Group({
  title,
  explain,
  count,
  children,
  testId,
}: {
  title: string;
  explain: string;
  count: number;
  children: ReactNode;
  testId: string;
}) {
  if (!count) return null;
  return (
    <details
      className="group border border-slate-200 rounded-md"
      data-testid={testId}
      open={count <= 5}
    >
      <summary className="cursor-pointer select-none px-3 py-2 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-900">{title}</span>
        <span className="text-xs font-semibold tabular-nums text-slate-700">{fmtN(count)}</span>
      </summary>
      <div className="px-3 pb-3">
        <p className="text-[11px] text-slate-500 mb-2">{explain}</p>
        {children}
      </div>
    </details>
  );
}

function PairList({ list, canAmend }: { list: ListOf<DqPairItem>; canAmend: boolean }) {
  return (
    <>
      <ul className="divide-y divide-slate-100">
        {list.items.map((p) => (
          <li key={`${p.dispatch.ticketUid}-${p.receipt.ticketUid}`} className="py-2 space-y-0.5">
            <div>
              <TicketLine t={p.dispatch} label="Dispatch" />
            </div>
            <div>
              <TicketLine t={p.receipt} label="Receipt" />
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-slate-500">
              <span className="font-medium text-slate-700">{regDiffLabel(p.regDiff)}</span>
              {p.transitMinutes !== null && <span>· transit {fmtTransit(p.transitMinutes)}</span>}
              <span>· tier {p.tier}</span>
              <Flags flags={p.flags} />
            </div>
            {p.amend.length > 0 && (
              <div className="flex flex-wrap gap-x-4 gap-y-1 pt-0.5">
                {p.amend.map((a) => (
                  <AmendLinkButton key={`${a.ticketUid}-${a.field}`} link={a} canAmend={canAmend} />
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
      <Truncated list={list} />
    </>
  );
}

function TicketList({ list, canAmend }: { list: ListOf<DqTicketItem>; canAmend: boolean }) {
  return (
    <>
      <ul className="divide-y divide-slate-100">
        {list.items.map((i) => (
          <li key={i.ticket.ticketUid} className="py-2 space-y-0.5">
            <TicketLine t={i.ticket} label={i.ticket.ticketType} />
            <Flags flags={i.flags} />
            {i.amend.length > 0 && (
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {i.amend.map((a) => (
                  <AmendLinkButton key={`${a.ticketUid}-${a.field}`} link={a} canAmend={canAmend} />
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
      <Truncated list={list} />
    </>
  );
}

function MissingList({ list, canAmend }: { list: ListOf<DqMissingItem>; canAmend: boolean }) {
  return (
    <>
      <ul className="divide-y divide-slate-100">
        {list.items.map((m) => (
          <li key={m.dispatch.ticketUid} className="py-2 space-y-1">
            <TicketLine t={m.dispatch} label="Dispatch" />
            <Flags flags={m.flags} />
            {m.candidates.length > 0 ? (
              <div className="ml-3 pl-3 border-l-2 border-slate-200 space-y-1">
                <p className="text-[11px] text-slate-500">
                  Receipts with no dispatch that could be this load:
                </p>
                {m.candidates.map((c) => (
                  <div key={c.receipt.ticketUid} className="space-y-0.5">
                    <TicketLine t={c.receipt} label="Receipt" />
                    <div className="text-[10px] text-slate-500">
                      {regDiffLabel(c.regDiff)} · {fmtTransit(c.gapMinutes)} later
                      {c.weightDiffPct !== null &&
                        ` · weight differs ${Math.abs(c.weightDiffPct).toFixed(1)}%`}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {c.amend.map((a) => (
                        <AmendLinkButton
                          key={`${a.ticketUid}-${a.field}`}
                          link={a}
                          canAmend={canAmend}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-slate-500 ml-3">
                No receipt on this route could be this load.
              </p>
            )}
          </li>
        ))}
      </ul>
      <Truncated list={list} />
    </>
  );
}

/** Needs review (double dispatch): D1, the truck's next dispatch D2 (and its receipt), the action. */
function NeedsReviewList({
  list,
  canAmend,
}: {
  list: ListOf<DqNeedsReviewItem>;
  canAmend: boolean;
}) {
  return (
    <>
      <ul className="divide-y divide-slate-100">
        {list.items.map((m) => (
          <li
            key={m.dispatch.ticketUid}
            className="py-2 space-y-1"
            data-testid={`needs-review-${m.dispatch.ticketUid}`}
          >
            <TicketLine t={m.dispatch} label="Dispatch" />
            {m.nextDispatch ? (
              <div className="ml-3 pl-3 border-l-2 border-amber-200 space-y-0.5">
                <p className="text-[11px] text-slate-500">
                  Dispatched again
                  {m.gapMinutes !== null && <> {fmtTransit(m.gapMinutes)} later</>}
                  {m.weightDiffPct !== null && (
                    <>, weight differs {Math.abs(m.weightDiffPct).toFixed(1)}%</>
                  )}
                  , before any receipt:
                </p>
                <TicketLine t={m.nextDispatch} label="Dispatch" />
                {m.nextDispatchReceipt && (
                  <div>
                    <TicketLine t={m.nextDispatchReceipt} label="received as" />
                  </div>
                )}
              </div>
            ) : null}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-0.5">
              <span className="text-[11px] font-semibold text-amber-800">
                Suggested: {m.action.label}
              </span>
              {m.amend.map((a) => (
                <AmendLinkButton key={`${a.ticketUid}-${a.field}`} link={a} canAmend={canAmend} />
              ))}
            </div>
          </li>
        ))}
      </ul>
      <Truncated list={list} />
    </>
  );
}

/** Tickets with an implausible date (before 2000 or in the future): check the date. */
function ImplausibleList({
  list,
  canAmend,
}: {
  list: ListOf<DqImplausibleItem>;
  canAmend: boolean;
}) {
  return (
    <>
      <ul className="divide-y divide-slate-100">
        {list.items.map((i) => (
          <li
            key={i.ticket.ticketUid}
            className="py-2 space-y-0.5"
            data-testid={`implausible-${i.ticket.ticketUid}`}
          >
            <TicketLine t={i.ticket} label={i.ticket.ticketType} />
            <div className="text-[10px] text-slate-500">
              {i.reason === 'before_2000' ? 'dated before 2000' : 'dated in the future'}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {i.amend.map((a) => (
                <AmendLinkButton key={`${a.ticketUid}-${a.field}`} link={a} canAmend={canAmend} />
              ))}
            </div>
          </li>
        ))}
      </ul>
      <Truncated list={list} />
    </>
  );
}

function DataQuality({ data, canAmend }: { data: RouteDrawerResponse; canAmend: boolean }) {
  const dq = data.dq;
  const s = dq.summary;
  const reg = dq.regKeyedIncorrectly;
  const dno = dq.deliveryNo;
  // ST-00218 P3: tickets probably keyed with the wrong location (absent in older answers)
  const loc = dq.locationSuggestions;
  const locCount = loc ? loc.summary.tickets + loc.summary.groupings : 0;
  const nothing = dqIssueCount(s) === 0 && dq.daysWithoutReceiptData.length === 0 && locCount === 0;
  // Engine 1.1.0 folds "possible re-weigh" into "Needs review — probable duplicate weighing"
  // (the flag is kept as an alias on exactly those loads), so the old list is shown only for a
  // 1.0.0 answer.
  const nr = dq.needsReview;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-700">
        <DqBadge indicator={s.indicator} />
        <span>
          Receipts with a usable delivery no:{' '}
          <span className="font-semibold">
            {s.usableDeliveryNoPct === null ? '—' : `${s.usableDeliveryNoPct.toFixed(1)}%`}
          </span>
        </span>
        {s.weightGap > 0 && <span>Weight differs by more than 20%: {fmtN(s.weightGap)}</span>}
      </div>
      {!canAmend && (
        <p className="text-[11px] text-slate-500">
          The suggested corrections are shown as text. Opening them in Amendments needs the
          Amendments permission.
        </p>
      )}
      {nothing && (
        <p className="text-sm text-slate-600 py-4 text-center">
          No data-quality findings for this period.
        </p>
      )}
      <Group
        testId="dq-reg-typo"
        title="Probable registration typo"
        explain="The delivery no matches, the registrations differ by one keystroke. Counted as matched; correct the registration."
        count={reg.tier1Typo.total}
      >
        <PairList list={reg.tier1Typo} canAmend={canAmend} />
      </Group>
      <Group
        testId="dq-reg-mismatch"
        title="Registration keyed incorrectly"
        explain="Matched on the delivery no only — the registrations are clearly different. Check which registration is right."
        count={reg.tier3Mismatch.total}
      >
        <PairList list={reg.tier3Mismatch} canAmend={canAmend} />
      </Group>
      <Group
        testId="dq-reg-unconfirmed"
        title="Unconfirmed — probable registration typo"
        explain="No delivery no ties these together, but the registrations differ by one keystroke, the times fit and the weights agree. Counted as received; correct the registration to confirm."
        count={reg.tier4Unconfirmed.total}
      >
        <PairList list={reg.tier4Unconfirmed} canAmend={canAmend} />
      </Group>
      <Group
        testId="dq-missing"
        title="Missing loads"
        explain="Dispatched, not received within the transit window, while the receiving site has data for that time. They stay in dispatched, so the variance shows them."
        count={dq.missing.total}
      >
        <MissingList list={dq.missing} canAmend={canAmend} />
      </Group>
      {nr && (
        <>
          <Group
            testId="dq-needs-review-duplicate"
            title="Needs review — probable duplicate weighing"
            explain="The truck was weighed out again at the same site within 2 hours at a similar weight, before any receipt; the later dispatch carries the receipt. Probably the same load weighed twice: exclude the duplicate ticket. Until then it stays in dispatched like a missing load."
            count={nr.probableDuplicateWeighing.total}
          >
            <NeedsReviewList list={nr.probableDuplicateWeighing} canAmend={canAmend} />
          </Group>
          <Group
            testId="dq-needs-review-again"
            title="Needs review — dispatched again before receipt"
            explain="The truck was dispatched again before this load was received. Open both tickets: one of them may be a wrong registration, a lost load or a ticket to exclude. Until then it stays in dispatched like a missing load."
            count={nr.dispatchedAgainBeforeReceipt.total}
          >
            <NeedsReviewList list={nr.dispatchedAgainBeforeReceipt} canAmend={canAmend} />
          </Group>
        </>
      )}
      {dq.implausibleTimestamps && (
        <Group
          testId="dq-implausible"
          title="Ticket date out of range"
          explain="These tickets are dated before 2000 or in the future (any time, not only this period). Load matching ignores their date when it works out from when the route has data. Correct the date in Amendments."
          count={dq.implausibleTimestamps.total}
        >
          <ImplausibleList list={dq.implausibleTimestamps} canAmend={canAmend} />
        </Group>
      )}
      <Group
        testId="dq-orphans"
        title="Receipts with no dispatch"
        explain="Received on this route, but no dispatch ticket matches them."
        count={dq.orphans.total}
      >
        <TicketList list={dq.orphans} canAmend={canAmend} />
      </Group>
      <Group
        testId="dq-dno-blank"
        title="Delivery no blank"
        explain="The receipt has no delivery no; it was matched by truck and time. Fill in the delivery no."
        count={dno.blank.total}
      >
        <PairList list={dno.blank} canAmend={canAmend} />
      </Group>
      <Group
        testId="dq-dno-notfound"
        title="Delivery no not found"
        explain="No dispatch has the receipt's delivery no; it was matched by truck and time. Correct the delivery no."
        count={dno.notFound.total}
      >
        <PairList list={dno.notFound} canAmend={canAmend} />
      </Group>
      <Group
        testId="dq-dno-elsewhere"
        title="Delivery no points to another load"
        explain="The receipt's delivery no belongs to a different dispatch; it was matched by truck and time. Correct the delivery no."
        count={dno.pointsElsewhere.total}
      >
        <PairList list={dno.pointsElsewhere} canAmend={canAmend} />
      </Group>
      <Group
        testId="dq-offroute"
        title="Received off-route"
        explain="Dispatched on this route but received at a location that is not one of its receipt locations. Left out of adjusted dispatched."
        count={dq.receivedOffRoute.items.total}
      >
        {dq.receivedOffRoute.hints.map((h, i) => (
          <p key={i} className="text-xs text-amber-800 mb-1">
            <HintText text={h.text} />
          </p>
        ))}
        <PairList list={dq.receivedOffRoute.items} canAmend={canAmend} />
      </Group>
      {loc && (
        <Group
          testId="dq-location"
          title="Location probably wrong"
          explain="The dispatch and the receipt of these loads disagree on a dispatch or receipt location, so the load counts on another route or none. Each suggestion names the ticket and the value to set, with the evidence; or, when many trucks go to a location the route does not list, the location to add to the route instead. Shown only: nothing changes until an amendment is saved and approved."
          count={locCount}
        >
          <LocationSuggestionsList panel={loc} canAmend={canAmend} showPermissionNote={false} />
        </Group>
      )}
      <Group
        testId="dq-late"
        title="Late"
        explain="Received later than the transit window. Still counted as matched."
        count={dq.late.total}
      >
        <PairList list={dq.late} canAmend={canAmend} />
      </Group>
      <Group
        testId="dq-reweigh"
        title="Possible re-weighs"
        explain="An unmatched dispatch followed within 2 hours by a matched dispatch of the same truck on this route — probably weighed twice."
        count={nr ? 0 : dq.possibleReweighs.total}
      >
        <TicketList list={dq.possibleReweighs} canAmend={canAmend} />
      </Group>
      <Group
        testId="dq-nodata-days"
        title="Days without receipt data"
        explain="Days with dispatches but no receipt on this route — the receiving site's data may be late or missing."
        count={dq.daysWithoutReceiptData.length}
      >
        <p className="text-xs text-slate-700">
          {dq.daysWithoutReceiptData.map((d) => formatMiningDate(`${d}T12:00:00+02:00`)).join(', ')}
        </p>
      </Group>
    </div>
  );
}

// ─────────────────────────────────────────────────────────── (d) sign-off ──

const ACTION_LABELS: Record<SignoffRecord['action'], string> = {
  signoff: 'Signed off',
  switch: 'Set to Truck + time',
  revert: 'Set to delivery number',
  window: 'Transit window changed',
};

/** A switch / revert refused because the route changed meanwhile (or is no longer allowed): reload. */
const STALE_CODES = ['already_engine', 'not_engine', 'signoff_required', 'route_inactive'];

function SignoffSection({
  data,
  mode,
  canManage,
  semanticChanges,
  onChanged,
  onFlipped,
}: {
  data: RouteDrawerResponse;
  mode: LoadMatchingMode;
  canManage: boolean;
  semanticChanges: SemanticChange[];
  onChanged: () => void;
  /** A switch / revert succeeded: show its result at once (the reload follows). */
  onFlipped: (res: SwitchResponse) => void;
}) {
  const route = data.route;
  const so = route.signoff;
  const [dialog, setDialog] = useState(false);
  const [confirm, setConfirm] = useState<'switch' | 'revert' | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const doFlip = async () => {
    if (!confirm) return;
    if (confirm === 'revert' && !note.trim()) {
      setError('Say why you are changing it back.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (confirm === 'switch') {
        onFlipped(await switchRoute(route.groupingId, note.trim() || undefined));
        setMessage(`${route.name} now uses Truck + time.`);
      } else {
        onFlipped(await revertRoute(route.groupingId, note.trim()));
        setMessage(`${route.name} now uses delivery-number matching.`);
      }
      setConfirm(null);
      setNote('');
      onChanged();
    } catch (err) {
      setError(loadMatchingErrorText(err, 'That did not work. Try again.'));
      const code = loadMatchingErrorCode(err);
      if (code && STALE_CODES.includes(code)) {
        setConfirm(null);
        onChanged();
      }
    } finally {
      setBusy(false);
    }
  };

  const downloadSigned = async (id: number, format: ExportFormat) => {
    setDownloading(`${id}-${format}`);
    setError(null);
    try {
      await downloadSignoffExport(id, format);
    } catch (err) {
      setError(loadMatchingErrorText(err, 'The document could not be downloaded.'));
    } finally {
      setDownloading(null);
    }
  };

  const last = so.lastSignoff;
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-slate-200 px-3 py-3 space-y-1">
        <div className="flex items-center gap-2">
          <SignoffBadge state={so.state} />
          <span className="text-xs text-slate-600">
            {route.loadMatching === 'engine'
              ? 'This route uses Truck + time (the new matching).'
              : 'This route uses delivery-number matching.'}
          </span>
        </div>
        {last ? (
          <p className="text-xs text-slate-700">
            Last signed off by <strong>{last.signedByName || last.signedBy}</strong>
            {last.signedByTitle && `, ${last.signedByTitle}`} on {formatAppDateTime(last.signedAt)}{' '}
            for {fmtPeriod({ startDate: last.periodStart, endDate: last.periodEnd })}.
          </p>
        ) : (
          <p className="text-xs text-slate-700">Nobody has signed off this route yet.</p>
        )}
        <p className="text-[11px] text-slate-500">
          The route&apos;s matching is its Load matching setting (Settings → Route Groupings).
          Signing off is optional: it records your review for the customer&apos;s file and changes
          nothing.
        </p>
        {mode === 'shadow' && (
          <p className="text-[11px] text-slate-500">
            Preview mode: pages show delivery-number figures on every route until load matching is
            switched on for this server, also on a route set to Truck + time.
          </p>
        )}
      </div>

      {canManage ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setDialog(true)}
            className="h-9 px-3 text-xs font-semibold rounded-md bg-primary-600 text-white hover:bg-primary-700"
          >
            Sign off this route
          </button>
          {route.loadMatching === 'legacy' && (
            <button
              type="button"
              disabled={!so.canSwitch}
              onClick={() => {
                setConfirm('switch');
                setError(null);
              }}
              title={so.switchBlockedReason ?? undefined}
              className="h-9 px-3 text-xs font-medium rounded-md border border-primary-300 text-primary-800 hover:bg-primary-50 disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-transparent"
            >
              Use Truck + time for this route
            </button>
          )}
          {so.canRevert && (
            <button
              type="button"
              onClick={() => {
                setConfirm('revert');
                setError(null);
              }}
              className="h-9 px-3 text-xs font-medium rounded-md border border-amber-300 text-amber-800 hover:bg-amber-50"
            >
              Use delivery-number matching
            </button>
          )}
          {!so.canSwitch && route.loadMatching === 'legacy' && so.switchBlockedReason && (
            <p className="w-full text-[11px] text-slate-500">{so.switchBlockedReason}.</p>
          )}
        </div>
      ) : (
        <p className="text-[11px] text-slate-500">
          You can review and export. Signing off and changing the route&apos;s matching need the
          Manage load matching permission.
        </p>
      )}

      {confirm && (
        <div
          className="rounded-md border border-slate-300 bg-slate-50 px-3 py-3 space-y-2"
          role="group"
          aria-label="Confirm"
        >
          <p className="text-sm text-slate-800">
            {confirm === 'switch'
              ? `Use Truck + time for ${route.name}? Its figures then come from the load matching. You can change it back here or in Settings → Route Groupings.`
              : `Use delivery-number matching (exact) for ${route.name}? Its figures then match a receipt's Delivery No to the dispatch Ticket No only.`}
          </p>
          <label htmlFor="lm-flip-note" className="block text-[11px] font-medium text-slate-600">
            {confirm === 'revert' ? 'Why are you changing it back? (required)' : 'Note (optional)'}
          </label>
          <textarea
            id="lm-flip-note"
            rows={2}
            maxLength={2000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full px-2 py-1.5 border border-slate-300 rounded-md bg-surface text-sm text-slate-900"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void doFlip()}
              className="h-8 px-3 text-xs font-semibold rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {busy
                ? 'Working…'
                : confirm === 'switch'
                  ? 'Yes, use Truck + time'
                  : 'Yes, use delivery numbers'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setConfirm(null);
                setNote('');
              }}
              className="h-8 px-3 text-xs font-medium rounded-md border border-slate-300 text-slate-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {message && <p className="text-xs text-emerald-700">{message}</p>}
      {error && (
        <p role="alert" className="text-xs text-red-700">
          {error}
        </p>
      )}

      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
          History
        </h4>
        {data.signoffs.length === 0 ? (
          <p className="text-xs text-slate-500">No sign-offs or matching changes yet.</p>
        ) : (
          <ul
            className="divide-y divide-slate-100 border-y border-slate-100"
            aria-label="Sign-off history"
          >
            {data.signoffs.map((r) => (
              <li key={r.signoffId} className="py-2 text-xs text-slate-700 space-y-0.5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-semibold text-slate-900">{ACTION_LABELS[r.action]}</span>
                  <span className="text-[11px] text-slate-500">
                    {formatAppDateTime(r.signedAt)}
                  </span>
                </div>
                <div>
                  {r.signedByName ? (
                    <>
                      {r.signedByName}
                      {r.signedByTitle && `, ${r.signedByTitle}`}
                      <span className="text-slate-500"> (entered by {r.signedBy})</span>
                    </>
                  ) : (
                    r.signedBy
                  )}
                  {r.action === 'signoff' && (
                    <span className="text-slate-500">
                      {' '}
                      · period {fmtPeriod({ startDate: r.periodStart, endDate: r.periodEnd })}
                    </span>
                  )}
                </div>
                {r.note && <p className="text-slate-600 italic">“{r.note}”</p>}
                {r.action === 'signoff' && (
                  <div className="flex gap-3 pt-0.5">
                    <button
                      type="button"
                      disabled={downloading !== null}
                      onClick={() => void downloadSigned(r.signoffId, 'pdf')}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-700 hover:underline"
                    >
                      <FaFilePdf className="w-3 h-3" />
                      {downloading === `${r.signoffId}-pdf`
                        ? 'Preparing…'
                        : 'Signed document (PDF)'}
                    </button>
                    <button
                      type="button"
                      disabled={downloading !== null}
                      onClick={() => void downloadSigned(r.signoffId, 'csv')}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-700 hover:underline"
                    >
                      <FaFileCsv className="w-3 h-3" />
                      {downloading === `${r.signoffId}-csv` ? 'Preparing…' : 'CSV'}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {dialog && (
        <SignoffDialog
          routes={[{ groupingId: route.groupingId, name: route.name }]}
          period={data.period}
          semanticChanges={semanticChanges}
          onClose={() => setDialog(false)}
          onSigned={() => onChanged()}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────── drawer ──

export default function RouteDrawer({
  groupingId,
  period,
  mode,
  canManage,
  semanticChanges,
  onClose,
  onChanged,
}: RouteDrawerProps) {
  const { hasPermission } = useAuth();
  const canAmend = hasPermission('amendments.view');
  const [data, setData] = useState<RouteDrawerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<DrawerTab>('changes');
  const [exporting, setExporting] = useState<ExportFormat | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(
    async (quiet = false) => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const res = await getRoute(groupingId, period, { signal: ac.signal });
        if (!ac.signal.aborted) setData(res);
      } catch (err) {
        if (ac.signal.aborted) return;
        setError(loadMatchingErrorText(err, 'This route could not be loaded.'));
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    },
    [groupingId, period]
  );

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      // A dialog on top handles its own Escape.
      if (e.key === 'Escape' && !document.querySelector('[aria-labelledby="lm-signoff-title"]'))
        onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const changed = () => {
    onChanged();
    void load(true);
  };

  // The switch / revert answer carries the new mode, window and record: apply it at once so the
  // buttons are right while the drawer reloads in the background.
  const applyFlip = (res: SwitchResponse) => {
    setData((d) => {
      if (!d || !res?.route) return d;
      const engine = res.route.loadMatching === 'engine';
      const so = d.route.signoff;
      const signoff = {
        ...so,
        state: engine ? ('switched' as const) : ('reverted' as const),
        canSwitch: !engine && canManage && !!so.lastSignoff,
        switchBlockedReason: engine ? 'The route already uses the new matching' : null,
        canRevert: engine && canManage,
        lastAction:
          res.record && res.record.action !== 'window'
            ? {
                signoffId: res.record.signoffId,
                action: res.record.action,
                periodStart: res.record.periodStart,
                periodEnd: res.record.periodEnd,
                signedBy: res.record.signedBy,
                signedByName: res.record.signedByName,
                signedByTitle: res.record.signedByTitle,
                signedAt: res.record.signedAt,
                engineVersion: res.record.engineVersion,
              }
            : so.lastAction,
      };
      const route = {
        ...d.route,
        loadMatching: res.route.loadMatching,
        window: res.route.window,
        signoff,
      };
      return {
        ...d,
        route,
        transit: { ...d.transit, window: res.route.window },
        signoffs: res.record ? [res.record, ...d.signoffs] : d.signoffs,
      };
    });
  };

  const exportDoc = async (format: ExportFormat) => {
    setExporting(format);
    setError(null);
    try {
      await downloadExport({ ...period, groupingIds: [groupingId], format });
    } catch (err) {
      setError(loadMatchingErrorText(err, 'The document could not be downloaded.'));
    } finally {
      setExporting(null);
    }
  };

  const onWindowSaved = (w: RouteWindow) => {
    setData((d) =>
      d
        ? {
            ...d,
            route: { ...d.route, window: w },
            transit: { ...d.transit, window: w },
            dq: { ...d.dq, window: w },
          }
        : d
    );
    onChanged();
  };

  const route = data?.route;
  /** Engine 1.1.0 answers carry the needs-review figures (a 1.0.0 answer has no column). */
  const hasReview = typeof route?.new.needsReviewN === 'number';
  const tabs: { id: DrawerTab; label: string }[] = [
    { id: 'changes', label: `Changed loads${data ? ` (${fmtN(data.changedTickets.total)})` : ''}` },
    {
      id: 'quality',
      label: `Data quality${data ? ` (${fmtN(dqIssueCount(data.dq.summary))})` : ''}`,
    },
    { id: 'transit', label: 'Transit time' },
    { id: 'signoff', label: 'Sign-off' },
  ];

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={route ? `Load matching: ${route.name}` : 'Load matching route'}
        className="h-full w-full max-w-4xl bg-surface shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-bold text-slate-900 truncate">
              {route?.name ?? 'Loading route…'}
            </h3>
            <p className="text-xs text-slate-500">
              {fmtPeriod(period)}
              {route && ` · ticket matching ${MATCH_MODE_LABELS[route.old.matchMode]}`}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              disabled={!data || exporting !== null}
              onClick={() => void exportDoc('pdf')}
              className="inline-flex items-center gap-1 h-8 px-2.5 text-[11px] font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              title="The document for review: figures, changed loads and the transit window"
            >
              <FaFilePdf className="w-3 h-3" /> {exporting === 'pdf' ? 'Preparing…' : 'PDF'}
            </button>
            <button
              type="button"
              disabled={!data || exporting !== null}
              onClick={() => void exportDoc('csv')}
              className="inline-flex items-center gap-1 h-8 px-2.5 text-[11px] font-medium rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <FaFileCsv className="w-3 h-3" /> {exporting === 'csv' ? 'Preparing…' : 'CSV'}
            </button>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="p-1 text-slate-400 hover:text-slate-600"
            >
              <FaTimes size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {loading && !data && (
            <div className="py-16 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-primary-500" />
              <p className="mt-3 text-xs text-slate-500">Matching this route's loads…</p>
            </div>
          )}
          {error && (
            <div
              role="alert"
              className="mb-3 p-3 rounded-md border border-red-200 bg-red-50 text-xs text-red-700"
            >
              {error}{' '}
              {!data && (
                <button type="button" className="underline" onClick={() => void load()}>
                  Retry
                </button>
              )}
            </div>
          )}
          {data && route && (
            <div className={loading ? 'opacity-60 transition-opacity' : ''}>
              <section aria-label="Route figures" className="mb-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                        <th className="text-left font-semibold py-1 pr-2" />
                        <th className="text-right font-semibold py-1 px-2">BF</th>
                        <th className="text-right font-semibold py-1 px-2">CF</th>
                        <th className="text-right font-semibold py-1 px-2">Missing</th>
                        {hasReview && (
                          <th
                            className="text-right font-semibold py-1 px-2"
                            title="Loads that need review: dispatched again before a receipt"
                          >
                            Review
                          </th>
                        )}
                        <th className="text-right font-semibold py-1 px-2">No disp.</th>
                        <th className="text-right font-semibold py-1 px-2">Adjusted</th>
                        <th className="text-right font-semibold py-1 pl-2">Variance</th>
                      </tr>
                    </thead>
                    <tbody className="tabular-nums">
                      <tr className="border-t border-slate-100 text-slate-600">
                        <th scope="row" className="text-left font-medium py-1.5 pr-2">
                          Now
                        </th>
                        <td className="text-right px-2">{fmtN(route.old.bfN)}</td>
                        <td className="text-right px-2">{fmtN(route.old.cfN)}</td>
                        <td className="text-right px-2">—</td>
                        {hasReview && <td className="text-right px-2">—</td>}
                        <td className="text-right px-2">—</td>
                        <td className="text-right px-2">{fmtT(route.old.adjustedT)}</td>
                        <td className="text-right pl-2">{fmtPct(route.old.variancePct)}</td>
                      </tr>
                      <tr className="border-t border-slate-100 font-semibold text-slate-900">
                        <th scope="row" className="text-left py-1.5 pr-2">
                          New
                        </th>
                        <td className="text-right px-2">
                          {isNa(route.new) ? 'n/a' : fmtN(route.new.bfN)}
                        </td>
                        <td className="text-right px-2">
                          {isNa(route.new) ? 'n/a' : fmtN(route.new.cfN)}
                        </td>
                        <td className="text-right px-2">
                          {isNa(route.new) ? 'n/a' : fmtN(route.new.missingN)}
                        </td>
                        {hasReview && (
                          <td className="text-right px-2" data-testid="drawer-needs-review">
                            {isNa(route.new) ? 'n/a' : fmtN(route.new.needsReviewN)}
                          </td>
                        )}
                        <td className="text-right px-2">{fmtN(route.new.orphanN)}</td>
                        <td className="text-right px-2">
                          {isNa(route.new) ? 'n/a' : fmtT(route.new.adjustedT)}
                        </td>
                        <td className="text-right pl-2">
                          {fmtPct(route.new.variancePct)}
                          {route.delta.variancePctPoints !== null && (
                            <span className="block text-[10px] font-normal text-slate-500">
                              {fmtPts(route.delta.variancePctPoints)}
                            </span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <RouteNotes row={route} />
                {!isNa(route.new) && (
                  <p className="mt-2 text-[11px] text-slate-600">
                    {bridgeSentence(route.new.bridge)}
                  </p>
                )}
                {route.new.matched.n > 0 && (
                  <div className="mt-3">
                    <p className="text-[11px] font-medium text-slate-600 mb-1">
                      How the {fmtN(route.new.matched.n)} matched loads were matched
                    </p>
                    <TierMixBar mix={route.tierMix} height={10} />
                    <div className="mt-1.5">
                      <TierLegend mix={route.tierMix} />
                    </div>
                  </div>
                )}
              </section>

              <div
                role="tablist"
                aria-label="Route details"
                className="flex gap-0.5 bg-slate-100 rounded-lg p-0.5 mb-4 overflow-x-auto"
              >
                {tabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.id}
                    onClick={() => setTab(t.id)}
                    className={`flex-1 sm:flex-none whitespace-nowrap px-3 py-1.5 text-[11px] sm:text-xs font-semibold rounded-md ${
                      tab === t.id
                        ? 'bg-surface text-slate-800 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div role="tabpanel">
                {tab === 'changes' && <ChangedLoads list={data.changedTickets} />}
                {tab === 'quality' && <DataQuality data={data} canAmend={canAmend} />}
                {tab === 'transit' && (
                  <div className="space-y-4">
                    <TransitHistogram transit={data.transit} />
                    <WindowPanel
                      key={route.groupingId}
                      window={route.window}
                      groupingId={route.groupingId}
                      canManage={canManage}
                      onSaved={onWindowSaved}
                    />
                  </div>
                )}
                {tab === 'signoff' && (
                  <SignoffSection
                    data={data}
                    mode={mode}
                    canManage={canManage}
                    semanticChanges={semanticChanges}
                    onChanged={changed}
                    onFlipped={applyFlip}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
